import React, { useEffect } from "react";
import { Checkbox, Table, Modal, Tooltip, Typography } from "antd";
import { AiOutlineClose } from "react-icons/ai";
import { MdOutlineArrowOutward } from "react-icons/md";

import PrimaryButton from "../Forms/PrimaryButton";
import { orderStatus } from "../../constant";
import { roundDecimal } from "../../utils";

const { Title } = Typography;

export default function ConfirmStatusModal({
  open,
  onClose,
  handleSubmit,
  title = "Do you want to update this order to production?",
  buttonTitle = "Update Status",
  showCancel = false,
  cancelText = "Cancel",
  loading,
  width = 420,
  orderData,
  subTitle,
}) {
  const alreadySelected = React.useMemo(() => {
    return (
      orderData?.items
        ?.filter((item) => item.status === orderStatus.production)
        ?.map((item) => item.order_item_id) || []
    );
  }, [orderData?.items]);

  const finalProdItems = React.useMemo(() => {
    return orderData?.items?.filter(
      (item) => item.status === orderStatus.placed || item.status === orderStatus.production,
    );
  }, [orderData?.items]);

  const [orderItems, setOrderItems] = React.useState([]);
  const orderDataStr = JSON.stringify(orderData);

  useEffect(() => {
    setOrderItems(alreadySelected);
  }, [alreadySelected, orderDataStr]);

  const onChange = (e, id) => {
    if (e.target.checked) {
      setOrderItems([...orderItems, id]);
    } else {
      setOrderItems(orderItems.filter((item) => item !== id));
    }
  };

  const columns = [
    {
      title: "Quality Name",
      dataIndex: "short_name",
      ellipsis: true,
    },
    {
      title: "Shade",
      dataIndex: "shade_name",
      ellipsis: true,
      width: 80,
    },
    {
      title: "Quantity",
      dataIndex: "cases",
      ellipsis: true,
      render: (_, record) =>
        `${record.update_total_cases || record.cases || "0"} cases (
          ${roundDecimal(record.update_total_meter || record.quantity_meters || "0")}m)`,
    },
    {
      title: (
        <Checkbox
          className="custom-checkbox"
          checked={orderItems?.length === finalProdItems?.length}
          onChange={(e) => {
            if (e.target.checked) {
              setOrderItems(finalProdItems.map((item) => item.order_item_id));
            } else {
              setOrderItems(alreadySelected);
            }
          }}
        />
      ),
      dataIndex: "action",
      align: "center",
      width: 35,
      render: (_, record) => {
        const disabled = alreadySelected?.includes(record.order_item_id);

        return (
          <Tooltip title={disabled ? "Already in production" : undefined}>
            <Checkbox
              className="custom-checkbox"
              disabled={disabled}
              checked={orderItems?.includes(record.order_item_id)}
              onChange={(e) => onChange(e, record.order_item_id)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Modal
      className="form-css-2 custom-modal status-modal"
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      width={width}
    >
      {/* Header */}
      <div className="flex flex-col justify-between">
        <Title
          level={2}
          className="text-black !mb-[8px] font-abhaya text-3xl bg-transparent text-left"
        >
          {title}
        </Title>
        {subTitle && <p className={`w-full text-lg font-medium mb-4`}>{subTitle}</p>}

        {finalProdItems?.length > 0 && (
          <>
            <Title level={5}>Select items to update to production</Title>

            <Table
              rowKey="order_item_id"
              columns={columns}
              dataSource={finalProdItems}
              pagination={false}
              size="middle"
              className="mb-3"
              rootClassName="transparent-table"
            />
          </>
        )}
      </div>

      {/* Footer */}
      <div className="w-full flex justify-center gap-12">
        {showCancel && (
          <PrimaryButton
            btnType="secondary"
            text={cancelText}
            onClick={onClose}
            loading={loading}
          />
        )}
        <div>
          <PrimaryButton
            text={buttonTitle}
            onClick={() => handleSubmit(orderItems)}
            loading={loading}
            className="me-[30px]"
          />
        </div>
      </div>
    </Modal>
  );
}
